import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import {
  useCreateStoreUserMutation,
  useDeleteStoreUserMutation,
  useGetStoreUserListQuery,
  useUpdateStoreUserMutation,
} from "@/features/business";
import { userRoleSelector } from "@/features/auth";
import { StorePermissionEnum, StoreRoleEnum } from "@/enum";
import { StoreUserRoleMapping } from "@/interfaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { Edit, MoreVertical, Plus, Search, Trash2, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { z } from "zod";

export const Route = createFileRoute("/(app)/(store)/staff/")({
  component: StaffManagementScreen,
});

type StaffManagementScreenProps = {
  embedded?: boolean;
};
type PermissionOverrides = Partial<Record<StorePermissionEnum, boolean>>;

type StaffFormData = {
  name: string;
  nationalNumber: string;
  countryCode: string;
  storeRole: StoreRoleEnum;
  title: string;
  permissionOverrides: PermissionOverrides;
};

const staffFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  nationalNumber: z
    .string()
    .trim()
    .min(10, "Phone number must be at least 10 digits"),
  countryCode: z.string().default("+91"),
  storeRole: z.nativeEnum(StoreRoleEnum, {
    required_error: "Role is required",
    invalid_type_error: "Role is required",
  }),
  title: z.string().trim().min(1, "Title is required"),
  permissionOverrides: z.record(z.boolean()).default({}),
});

const defaultFormValues: StaffFormData = {
  name: "",
  nationalNumber: "",
  countryCode: "+91",
  storeRole: "" as StoreRoleEnum,
  title: "",
  permissionOverrides: {},
};

// Only Managers can be granted per-user permission overrides; the role ceiling
// (which permissions a Manager may receive) is enforced by the backend.
// SaleView + SaleCreate are Manager defaults (not toggleable) — edit/cancel/return
// are admin-grantable overrides.
const managerPermissionFields: {
  permission: StorePermissionEnum;
  label: string;
  description: string;
}[] = [
  {
    permission: StorePermissionEnum.SaleEdit,
    label: "Edit Sales",
    description: "This manager can edit existing orders.",
  },
  {
    permission: StorePermissionEnum.SaleCancel,
    label: "Cancel Sales",
    description: "This manager can cancel orders.",
  },
  {
    permission: StorePermissionEnum.SaleReturn,
    label: "Process Returns",
    description: "This manager can process return orders.",
  },
];

// The form holds toggles as a map for easy binding; the backend stores/receives
// only the granted permissions as an array. These convert between the two.
const buildFormOverrides = (
  role: StoreRoleEnum | string,
  granted?: StorePermissionEnum[],
): PermissionOverrides => {
  if (role === StoreRoleEnum.Manager) {
    return managerPermissionFields.reduce<PermissionOverrides>((acc, field) => {
      acc[field.permission] = granted?.includes(field.permission) ?? false;
      return acc;
    }, {});
  }
  return {};
};

const toGrantedArray = (
  role: StoreRoleEnum | string,
  overrides?: PermissionOverrides,
): StorePermissionEnum[] => {
  if (role !== StoreRoleEnum.Manager) {
    return [];
  }
  return managerPermissionFields
    .filter((field) => overrides?.[field.permission])
    .map((field) => field.permission);
};

const roleDescriptions: Record<StoreRoleEnum, string> = {
  [StoreRoleEnum.Owner]: "Full control over store, users, products, and orders.",
  [StoreRoleEnum.Admin]: "Can manage store users, products, and order operations.",
  [StoreRoleEnum.Manager]: "Can manage order operations based on assigned permissions.",
  [StoreRoleEnum.Employee]: "Limited store access.",
};

const getRoleOptions = (currentRole?: StoreRoleEnum) => {
  const roles =
    currentRole === StoreRoleEnum.Owner
      ? [
          StoreRoleEnum.Owner,
          StoreRoleEnum.Admin,
          StoreRoleEnum.Manager,
          StoreRoleEnum.Employee,
        ]
      : [StoreRoleEnum.Admin, StoreRoleEnum.Manager, StoreRoleEnum.Employee];

  return roles.map((role) => ({
    value: role,
    label: role,
    description: roleDescriptions[role],
  }));
};

const getErrorMessage = (error: unknown, fallback: string) =>
  (error as { data?: { message?: string }; message?: string })?.data?.message ||
  (error as { message?: string })?.message ||
  fallback;

export function StaffManagementScreen({ embedded = false }: StaffManagementScreenProps) {
  const currentRole = useSelector(userRoleSelector) as StoreRoleEnum | undefined;
  const { data: staffMembers = [], isFetching, isError, refetch } =
    useGetStoreUserListQuery(undefined, { refetchOnFocus: true });
  const [createStoreUser, { isLoading: isCreating }] =
    useCreateStoreUserMutation();
  const [updateStoreUser, { isLoading: isUpdating }] =
    useUpdateStoreUserMutation();
  const [deleteStoreUser, { isLoading: isDeleting }] =
    useDeleteStoreUserMutation();
  const [searchText, setSearchText] = useState("");
  const [activeStaffId, setActiveStaffId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StoreUserRoleMapping | null>(
    null,
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<StaffFormData>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(staffFormSchema),
    mode: "onBlur",
  });

  const selectedRole = watch("storeRole");
  const roleOptions = useMemo(() => getRoleOptions(currentRole), [currentRole]);
  const isManagerSelected = selectedRole === StoreRoleEnum.Manager;
  const isSubmitting = isCreating || isUpdating;

  const filteredStaff = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return staffMembers;
    }

    return staffMembers.filter((staff) => {
      const userName = staff.user?.name ?? "";
      const phoneNumber = staff.user?.phoneNumber?.nationalNumber ?? "";
      const role = staff.role ?? "";
      const title = staff.title ?? "";
      return [userName, phoneNumber, role, title]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [searchText, staffMembers]);

  const applyRolePermissionDefaults = (
    nextRole: StoreRoleEnum,
    previousRole?: StoreRoleEnum,
  ) => {
    const shouldPreserveExistingPermissions = previousRole === nextRole;
    setValue(
      "permissionOverrides",
      shouldPreserveExistingPermissions
        ? getValues("permissionOverrides")
        : buildFormOverrides(nextRole),
      { shouldDirty: true },
    );
  };

  const openCreateModal = () => {
    setEditingStaff(null);
    reset(defaultFormValues);
    setIsFormOpen(true);
  };

  const openEditModal = (staff: StoreUserRoleMapping) => {
    setEditingStaff(staff);
    reset({
      name: staff.user?.name ?? "",
      nationalNumber: staff.user?.phoneNumber?.nationalNumber ?? "",
      countryCode: staff.user?.phoneNumber?.countryCode ?? "+91",
      storeRole: staff.role,
      title: staff.title ?? "",
      permissionOverrides: buildFormOverrides(
        staff.role,
        staff.permissionOverrides,
      ),
    });
    setActiveStaffId(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingStaff(null);
    reset(defaultFormValues);
  };

  const handleDeleteStaff = async (staff: StoreUserRoleMapping) => {
    const confirmed = window.confirm(
      `Remove ${staff.user?.name || "this staff member"} from this store?`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteStoreUser(staff.userId).unwrap();
      toast.success("Staff member removed successfully");
      setActiveStaffId(null);
      await refetch();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove staff member"));
    }
  };

  const onSubmit = async (data: StaffFormData) => {
    const permissionOverrides = toGrantedArray(
      data.storeRole,
      data.permissionOverrides,
    );
    const payload = {
      ...data,
      countryCode: data.countryCode || "+91",
      storeRole: data.storeRole as StoreRoleEnum,
      permissionOverrides,
    };

    try {
      if (editingStaff) {
        await updateStoreUser({
          userId: editingStaff.userId,
          name: payload.name,
          storeRole: payload.storeRole,
          title: payload.title,
          permissionOverrides: payload.permissionOverrides,
        }).unwrap();
        toast.success("Staff member updated successfully");
      } else {
        await createStoreUser(payload).unwrap();
        toast.success("Staff member added successfully");
      }

      closeForm();
      await refetch();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save staff member"));
    }
  };

  return (
    <div className={embedded ? "w-full" : "mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-6"}>
      {!embedded && (
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-200">
              <Users size={16} />
              Staff Management
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Store Staff
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add staff, assign roles, and control manager order permissions.
            </p>
          </div>
          <Button onClick={openCreateModal} className="w-full md:w-auto">
            <Plus size={16} />
            Add Staff
          </Button>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 dark:border-gray-800 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Staff Members
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredStaff.length} member{filteredStaff.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
            <div className="relative w-full md:w-64">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search staff..."
                className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
              />
            </div>
            {embedded && (
              <Button onClick={openCreateModal} className="w-full md:w-auto">
                <Plus size={16} />
                Add Staff
              </Button>
            )}
          </div>
        </div>

        {isFetching && staffMembers.length === 0 ? (
          <div className="flex items-center justify-center p-10 text-sm text-gray-500">
            Loading staff members...
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
            <p className="text-sm text-gray-500">Failed to load staff members.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-10 text-center">
            <div className="mb-3 rounded-full bg-gray-100 p-3 text-gray-500 dark:bg-gray-800">
              <Users size={24} />
            </div>
            <p className="font-medium text-gray-900 dark:text-white">
              No staff found
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Add your first staff member to manage store access.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredStaff.map((staff) => (
              <div
                key={staff._id}
                className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-200">
                    {(staff.user?.name || staff.role || "S").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {staff.user?.name || "Unnamed Staff"}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {staff.user?.phoneNumber?.countryCode || "+91"}{" "}
                      {staff.user?.phoneNumber?.nationalNumber || "No phone"}
                    </p>
                    {staff.title && (
                      <p className="mt-1 text-sm text-gray-500">{staff.title}</p>
                    )}
                    {staff.role === StoreRoleEnum.Manager && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        {managerPermissionFields.map((field) => (
                          <span
                            key={field.permission}
                            className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                          >
                            {field.label}:{" "}
                            {staff.permissionOverrides?.includes(field.permission)
                              ? "Allowed"
                              : "Not Allowed"}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 md:justify-end">
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-200">
                    {staff.role}
                  </span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setActiveStaffId((current) =>
                          current === staff._id ? null : staff._id,
                        )
                      }
                      className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white"
                      aria-label="Open staff actions"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {activeStaffId === staff._id && (
                      <div className="absolute right-0 z-20 mt-2 w-40 rounded-xl border border-gray-100 bg-white p-2 shadow-lg dark:border-gray-800 dark:bg-gray-900">
                        <button
                          type="button"
                          onClick={() => openEditModal(staff)}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          <Edit size={15} />
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={isDeleting}
                          onClick={() => handleDeleteStaff(staff)}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950"
                        >
                          <Trash2 size={15} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={isFormOpen}
        onClose={closeForm}
        className="max-w-2xl p-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingStaff ? "Edit Staff" : "Add Staff"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Assign a store role and manager order permissions.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name
              </label>
              <input
                {...register("name")}
                placeholder="Enter staff name"
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone Number
              </label>
              <div className="flex gap-2">
                <input
                  {...register("countryCode")}
                  className="h-10 w-20 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
                />
                <input
                  {...register("nationalNumber")}
                  placeholder="9876543210"
                  className="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
                />
              </div>
              {errors.nationalNumber && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.nationalNumber.message}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Role
              </label>
              <Controller
                control={control}
                name="storeRole"
                render={({ field: { value, onChange } }) => (
                  <select
                    value={value || ""}
                    onChange={(event) => {
                      const nextRole = event.target.value as StoreRoleEnum;
                      const previousRole = getValues("storeRole");
                      onChange(nextRole);
                      applyRolePermissionDefaults(nextRole, previousRole);
                    }}
                    className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
                  >
                    <option value="">Select role</option>
                    {roleOptions.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {selectedRole && (
                <p className="mt-1 text-xs text-gray-500">
                  {roleDescriptions[selectedRole as StoreRoleEnum]}
                </p>
              )}
              {errors.storeRole && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.storeRole.message}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Title
              </label>
              <input
                {...register("title")}
                placeholder="Assistant Manager"
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
              )}
            </div>
          </div>

          {isManagerSelected && (
            <div className="grid gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/40">
              {managerPermissionFields.map((field) => (
                <PermissionToggle
                  key={field.permission}
                  control={control}
                  permission={field.permission}
                  label={field.label}
                  description={field.description}
                />
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={closeForm}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || (!!editingStaff && !isDirty)}>
              {isSubmitting
                ? "Saving..."
                : editingStaff
                  ? "Update Staff"
                  : "Add Staff"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function PermissionToggle({
  control,
  permission,
  label,
  description,
}: {
  control: any;
  permission: StorePermissionEnum;
  label: string;
  description: string;
}) {
  return (
    <Controller
      control={control}
      name={`permissionOverrides.${permission}`}
      render={({ field: { value, onChange } }) => (
        <label className="flex cursor-pointer items-start justify-between gap-3">
          <span>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {label}
            </span>
            <span className="mt-0.5 block text-xs text-gray-500">
              {description}
            </span>
          </span>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => onChange(event.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        </label>
      )}
    />
  );
}
